import React, { useEffect, useState, useMemo, useId } from "react";
import { RWA_COLORS } from "~/styles/colors";
import { hexToRGB } from "~/utils/hexToRGB";

// // // //

interface GlobeLoaderProps {
    size?: number;
    color?: string;
    backColor?: string;
    spinnerColor?: string;
    strokeWidth?: number;
    globeSpeed?: number;
    spinnerSpeed?: number;
    spinnerScale?: number;
    latitudeLines?: number;
    longitudeLines?: number;
    animate?: boolean;
}

/**
 * Integrated Globe Loader Component
 * Combines a rotating geometric globe with an outer spinner ring.
 */
export function LogoLoadingSpinner(props: GlobeLoaderProps) {
    const {
        size = 200,
        color = RWA_COLORS.slate[500],
        backColor = hexToRGB(RWA_COLORS.slate[500], 0.3),
        spinnerColor = RWA_COLORS.slate[500],
        strokeWidth = 1.2,
        globeSpeed = 10,
        spinnerSpeed = 4,
        spinnerScale = 1.4,
        latitudeLines = 3,
        longitudeLines = 8,
        animate = true,
    } = props;

    return (
        <div
            style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: size * spinnerScale,
                height: size * spinnerScale,
            }}
        >
            <GlobeSpinner
                size={size * spinnerScale}
                color={spinnerColor}
                speed={spinnerSpeed}
                className="absolute"
            />
            <RotatingGlobe
                size={size}
                color={color}
                backColor={backColor}
                strokeWidth={strokeWidth}
                speed={globeSpeed}
                animate={animate}
                latitudeLines={latitudeLines}
                longitudeLines={longitudeLines}
            />
        </div>
    );
}

// --- Sub-components ---

interface RotatingGlobeProps {
    size: number;
    color: string;
    backColor: string;
    strokeWidth: number;
    speed: number;
    animate: boolean;
    latitudeLines: number;
    longitudeLines: number;
    className?: string;
}

/**
 * Rotating globe visualization with latitude and longitude lines.
 */
function RotatingGlobe(props: RotatingGlobeProps) {
    const {
        size,
        color,
        backColor,
        strokeWidth,
        speed,
        animate,
        latitudeLines,
        longitudeLines,
        className = "",
    } = props;

    const id = useId();
    const clipId = `globe-clip-${id.replace(/:/g, "")}`;
    const [phase, setPhase] = useState(0);

    useEffect(() => {
        if (!animate) return;
        let frameId: number;
        const startTime = performance.now();
        const loop = (time: number) => {
            const elapsed = (time - startTime) / 1000;
            setPhase((elapsed * Math.PI) / speed);
            frameId = requestAnimationFrame(loop);
        };
        frameId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frameId);
    }, [animate, speed]);

    const latitudes = useMemo(() => {
        const lines: React.ReactNode[] = [];
        const step = 20 / (latitudeLines + 1);
        for (let i = 1; i <= latitudeLines; i++) {
            const y = 2 + step * i;
            lines.push(<line key={i} x1="0" y1={y} x2="24" y2={y} />);
        }
        return lines;
    }, [latitudeLines]);

    const arcs = useMemo(() => {
        const step = Math.PI / (longitudeLines / 2);
        const back: React.ReactNode[] = [];
        const front: React.ReactNode[] = [];
        for (let i = 0; i < longitudeLines; i++) {
            const angle = (phase + i * step) % (2 * Math.PI);
            const x = 10 * Math.sin(angle);
            const z = 10 * Math.cos(angle);
            const sweep = x >= 0 ? 1 : 0;
            const rx = Math.max(0.1, Math.abs(x));
            const perspective = Math.abs(z) / 10;
            const path = (
                <path
                    key={i}
                    d={`M 12 2 A ${rx.toFixed(3)} 10 0 0 ${sweep} 12 22`}
                    strokeWidth={(
                        strokeWidth *
                        (0.4 + 0.6 * perspective)
                    ).toFixed(3)}
                    opacity={(0.6 + 0.4 * perspective).toFixed(3)}
                />
            );
            if (z > 0) front.push(path);
            else back.push(path);
        }
        return { back, front };
    }, [phase, longitudeLines, strokeWidth]);

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={strokeWidth}
            className={className}
        >
            <defs>
                <clipPath id={clipId}>
                    <circle cx="12" cy="12" r="10" />
                </clipPath>
            </defs>
            <g clipPath={`url(#${clipId})`}>
                <g stroke={color}>{latitudes}</g>
                <g stroke={backColor}>{arcs.back}</g>
                <g stroke={color}>{arcs.front}</g>
            </g>
            <circle cx="12" cy="12" r="10" stroke={color} />
        </svg>
    );
}

interface GlobeSpinnerProps {
    size: number;
    color: string;
    speed: number;
    className: string;
}

/**
 * Circular spinner ring that wraps around the globe.
 */
function GlobeSpinner(props: GlobeSpinnerProps) {
    const { size, color, speed, className } = props;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width={size}
            height={size}
            fill="none"
            className={`animate-spin ${className}`}
            style={{ color, animationDuration: `${speed}s` }}
        >
            <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="1.5"
                opacity="0.25"
            />
            <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="butt"
                opacity="0.75"
                strokeDasharray="15.71 62.83"
            />
        </svg>
    );
}
